/* eslint-disable @typescript-eslint/no-explicit-any */
export function convertToInt(value: string, defaultValue: number) {
  const num = Number(value);
  if (isNaN(num) || num === 0) {
    return defaultValue;
  }
  return num;
}

export function convertToBool(value: string): boolean {
  return value?.toLowerCase() === "true";
}

export function isNumber(value: string) {
  if (convertToInt(value, -1) === -1) return false;
  return true;
}

export function ExtractNumber(value: string, defaultValue: number) {
  const matches = value.match(/[+-]?\d+(?:\.\d+)?/g);
  if (matches !== null && matches.length === 1) {
    return convertToInt(matches[0], defaultValue);
  }
  return defaultValue;
}

export function GetBrowserInfo() {
  const ua = navigator.userAgent;
  const match = ua.match(/(opera|chrome|safari|firefox|msie|trident(?=\/))\/?\s*(\d+)/i);
  if (match === null) {
    return { name: navigator.appName, version: navigator.appVersion };
  }

  const [, browserName, browserVersion] = match;
  if (/trident/i.test(browserName)) {
    const [, ieVersion = ""] = /\brv[ :]+(\d+)/g.exec(ua);
    return { name: "IE", version: ieVersion };
  }

  if (/Chrome/i.test(browserName)) {
    const operaEdgeMatch = ua.match(/\b(OPR|Edge)\/(\d+)/);
    if (operaEdgeMatch !== null) return { name: operaEdgeMatch[1].replace("OPR", "Opera"), version: operaEdgeMatch[2] };
  }

  const versionMatch = ua.match(/version\/(\d+)/i);
  const version = versionMatch ? versionMatch[1] : browserVersion;

  return { name: browserName, version };
}

export function isIE() {
  return GetBrowserInfo().name === "IE";
}

export function GetViewPortSize() {
  const elem = document.compatMode === "CSS1Compat" ? document.documentElement : document.body;

  return {
    x: Math.max(elem.clientWidth, window.innerWidth || 0),
    y: Math.max(elem.clientHeight, window.innerHeight || 0),
  };
}

export const KEY_CODES = {
  ENTER: 13,
  SPACE: 32,
  TAB: 9,
  ESC: 27,
};

export interface ThrottleOpts {
  leading?: boolean;
  trailing?: boolean;
}
export interface Cancelable {
  cancel(): void;
}

export function Debounce<T extends (args: any) => unknown>(callback: T, ms: number) {
  let timer: NodeJS.Timeout;
  return function (...args: any[]) {
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const self = this;
    clearTimeout(timer);
    timer = setTimeout(() => callback.apply(self, args), ms);
  };
}

// https://github.com/jashkenas/underscore/blob/master/underscore.js#L837
export function Throttle<T extends (args: any) => unknown>(func: T, wait: number, options?: ThrottleOpts) {
  let timeout: NodeJS.Timeout;
  let context: any;
  let args: any;
  let result: any;
  let previous = 0;
  if (!options) options = {};

  const later = function () {
    previous = options.leading === false ? 0 : new Date().getTime();
    timeout = null;
    result = func.apply(context, args);
    if (!timeout) context = args = null;
  };

  const throttled = function (...args: any[]) {
    const now = new Date().getTime();
    if (!previous && options.leading === false) previous = now;
    const remaining = wait - (now - previous);
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    context = this;
    if (remaining <= 0 || remaining > wait) {
      if (timeout) {
        clearTimeout(timeout);
        timeout = null;
      }
      previous = now;
      result = func.apply(context, args);
      if (!timeout) context = args = null;
    } else if (!timeout && options.trailing !== false) {
      timeout = setTimeout(later, remaining);
    }
    return result;
  };

  throttled.cancel = function () {
    clearTimeout(timeout);
    previous = 0;
    timeout = context = args = null;
  };

  return throttled;
}

export function isEmptyString(str: string): boolean {
  return !str || 0 === str.length || str.trim().length === 0;
}

export function trimAny(str: string, chars: string) {
  let start = 0;
  let end = str.length;

  while (start < end && chars.indexOf(str[start]) >= 0) ++start;

  while (end > start && chars.indexOf(str[end - 1]) >= 0) --end;

  return start > 0 || end < str.length ? str.substring(start, end) : str;
}

export function insertAfter(newNode: Element, referenceNode: Element) {
  referenceNode.parentNode.insertBefore(newNode, referenceNode.nextElementSibling);
}

export function getDocument(event: CustomEvent): Document {
  return event.detail?.document === undefined ? document : event.detail.document;
}

export const htmlDecode = (input: string) => {
  const doc = new DOMParser().parseFromString(input, "text/html");
  return doc.documentElement.textContent;
};
